import { useState } from "react";
import { toast } from "sonner";
import { LogIn, UserPlus, ArrowLeft } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { GoogleIcon } from "@/components/GoogleIcon";

export function RsvpAuthGate({ onLogin, onRegister, defaultEmail, redirectTo, onBeforeGoogle, onNoAccount, allowNoAccount = true, allowAccount = true, defaultFirst = "", defaultLast = "" }) {
  const [step, setStep] = useState(allowAccount ? "choose" : "noaccount"); // choose | account | noaccount
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");
  const [first, setFirst] = useState(defaultFirst);
  const [last, setLast] = useState(defaultLast);
  const [naEmail, setNaEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (!email || !password || (mode === "register" && !name)) { toast.error("Please fill in all fields."); return; }
    setBusy(true);
    try {
      if (mode === "register") {
        await onRegister(name, email, password, redirectTo);
        toast.success("Please check your inbox to verify your email address before you can complete your RSVP.");
      } else {
        await onLogin(email, password);
        toast.success("You're signed in.");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const goNoAccount = async () => {
    if (!first.trim() || !last.trim() || !naEmail.trim()) { toast.error("Please enter your first name, last name and email."); return; }
    setBusy(true);
    try { await onNoAccount({ first_name: first.trim(), last_name: last.trim(), email: naEmail.trim() }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const startGoogle = () => {
    if (onBeforeGoogle) onBeforeGoogle();
    sessionStorage.setItem("postGoogleRedirect", redirectTo || "/app");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth/callback")}`;
  };

  const Shell = ({ children }) => (
    <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 sm:p-8 fade-up" data-testid="rsvp-auth-gate">{children}</div>
  );

  if (step === "choose") {
    return (
      <Shell>
        <div className="w-12 h-12 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86] mx-auto mb-4"><LogIn size={20} /></div>
        <h2 className="font-serif text-2xl text-center">How would you like to RSVP?</h2>
        <p className="text-sm text-stone-500 text-center mt-2">Sign in to save this event and your photos to your Evermore memories forever — or RSVP right away without an account.</p>
        <div className="mt-6 space-y-3">
          <button onClick={() => setStep("account")} data-testid="rsvp-choice-account"
            className="w-full flex items-center gap-3 rounded-xl border border-[#8C9A86] bg-[#8C9A86]/8 px-4 py-4 text-left hover:bg-[#8C9A86]/15 transition-colors">
            <LogIn size={18} className="text-[#5f6b59] shrink-0" />
            <span><span className="block text-sm font-medium text-stone-800">Sign in / Create Account</span><span className="block text-xs text-stone-500">Keep the event & photos in your memories.</span></span>
          </button>
          {allowNoAccount && (
            <button onClick={() => setStep("noaccount")} data-testid="rsvp-choice-noaccount"
              className="w-full flex items-center gap-3 rounded-xl border border-[#E8E6E1] px-4 py-4 text-left hover:bg-stone-50 transition-colors">
              <UserPlus size={18} className="text-stone-500 shrink-0" />
              <span><span className="block text-sm font-medium text-stone-800">RSVP Without Creating an Account</span><span className="block text-xs text-stone-500">Just your name & email — quick and simple.</span></span>
            </button>
          )}
        </div>
      </Shell>
    );
  }

  if (step === "noaccount") {
    return (
      <Shell>
        {allowAccount && <button onClick={() => setStep("choose")} data-testid="rsvp-na-back" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-4"><ArrowLeft size={15} /> Back</button>}
        <h2 className="font-serif text-2xl text-center">RSVP without an account</h2>
        <p className="text-sm text-stone-500 text-center mt-2">Please share your name and email so your host knows who's responding.</p>
        <div className="space-y-3 mt-6">
          <div className="grid grid-cols-2 gap-3">
            <GateField label="First name" value={first} onChange={setFirst} testid="rsvp-na-first" />
            <GateField label="Last name" value={last} onChange={setLast} testid="rsvp-na-last" />
          </div>
          <GateField label="Email" type="email" value={naEmail} onChange={setNaEmail} testid="rsvp-na-email" />
        </div>
        <button onClick={goNoAccount} disabled={busy} data-testid="rsvp-na-continue"
          className="mt-6 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
          {busy ? "Please wait…" : "Continue to RSVP"}
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <button onClick={() => setStep("choose")} data-testid="rsvp-account-back" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-4"><ArrowLeft size={15} /> Back</button>
      <h2 className="font-serif text-2xl text-center">{mode === "register" ? "Create your free account to RSVP" : "Sign in to RSVP"}</h2>
      <p className="text-sm text-stone-500 text-center mt-2">For your security, this RSVP is tied to a verified account. This also keeps the event details and photos in your Evermore memories forever.</p>
      <button onClick={startGoogle} data-testid="google-signin-btn" className="mt-6 w-full flex items-center justify-center gap-2.5 rounded-full border border-[#E8E6E1] bg-white py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors">
        <GoogleIcon /> Continue with Google
      </button>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-[#E8E6E1]" /><span className="text-xs text-stone-400">or use your email</span><div className="h-px flex-1 bg-[#E8E6E1]" />
      </div>
      <div className="space-y-3">
        {mode === "register" && <GateField label="Your name" value={name} onChange={setName} testid="auth-name" />}
        <GateField label="Email" type="email" value={email} onChange={setEmail} testid="auth-email" />
        <GateField label="Password" type="password" value={password} onChange={setPassword} testid="auth-password" />
      </div>
      <button onClick={go} disabled={busy} data-testid="auth-submit"
        className="mt-6 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
        {busy ? "Please wait…" : (mode === "register" ? "Create account & continue" : "Sign in & continue")}
      </button>
      <p className="text-center text-sm text-stone-500 mt-4">
        {mode === "register" ? "Already have an account?" : "New to Evermore?"}{" "}
        <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="auth-toggle" className="text-[#8C9A86] hover:underline">
          {mode === "register" ? "Sign in" : "Create one"}
        </button>
      </p>
    </Shell>
  );
}

function GateField({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
